/**

 * First-run, cross-page, and replay coordination for guided walkthroughs.

 * @module features/walkthrough/walkthroughOrchestrator

 */



import { t } from './i18n/walkthroughI18n';

import { buildNavContext } from '../../navigation/navConfig';

import { getDefaultDashboardPath, resolvePrimaryRole, ROLE_CODES } from '../../utils/rolePriority';

import { extractWalkthroughAnchorId } from './walkthroughAnchors';

import { resolveTargetSelector } from './components/walkthroughPosition';

import { navItemPath } from './tours/navTourHelpers';

import { getTourForUser, getToursForUser } from './tours/tourRegistry';

import { isTourCompleted } from './walkthroughStorage';

import { WALKTHROUGH_STORAGE_VERSION } from './walkthroughTypes';



export const WALKTHROUGH_REPLAY_KEY = 'kl_walkthrough_replay';

export const WALKTHROUGH_STEP_CHAPTER_BREAK = 'chapter-break';

export const WAIT_FOR_SELECTOR_TIMEOUT_MS = 5000;

export const PAGE_READY_TIMEOUT_MS = 15000;

export const WALKTHROUGH_ROUTE_POLL_MS = 50;



const MOBILE_NAV_EXEMPT_ANCHORS = new Set(['nav-mobile-menu', 'theme-profile']);

const PAGE_LOADER_SELECTOR = '.loading-spinner[aria-busy="true"]';



const isDevEnvironment =

  typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production';



/** @type {import('./walkthroughTypes').WalkthroughSessionState | null} */

let activeWalkthroughSession = null;



/**

 * @param {import('./walkthroughTypes').WalkthroughSessionState | null} session

 */

export function setWalkthroughSession(session) {

  activeWalkthroughSession = session;

}



export function clearWalkthroughSession() {

  activeWalkthroughSession = null;

}



/**

 * @returns {import('./walkthroughTypes').WalkthroughSessionState | null}

 */

export function getWalkthroughSession() {

  return activeWalkthroughSession;

}



/**

 * @param {string} pathname

 * @returns {boolean}

 */

export function shouldSuppressRoleRedirect(pathname) {

  if (!activeWalkthroughSession?.active || !pathname) return false;



  return activeWalkthroughSession.allowedRoutes.some(

    (route) => pathname === route || pathname.startsWith(`${route}/`)

  );

}



/**

 * @param {object|null|undefined} user

 * @param {import('./walkthroughTypes').WalkthroughPersistedState} persistedState

 * @returns {boolean}

 */

export function hasCompletedFirstRunWalkthrough(user, persistedState) {

  if (!user || !persistedState) return false;

  if (persistedState.version !== WALKTHROUGH_STORAGE_VERSION) return false;

  if (persistedState.dismissedAt) return true;



  const tours = getToursForUser(user);

  const firstTour = tours[0];

  if (!firstTour) return true;



  return isTourCompleted(persistedState, firstTour);

}



/**

 * @param {object|null|undefined} user

 * @param {import('./walkthroughTypes').WalkthroughPersistedState} persistedState

 * @returns {boolean}

 */

export function shouldAutoStartWalkthrough(user, persistedState) {

  if (!user || !persistedState) return false;

  return !hasCompletedFirstRunWalkthrough(user, persistedState);

}



/**

 * @param {object|null|undefined} user

 * @returns {string|null}

 */

export function getDefaultAutoStartTourId(user) {

  return getToursForUser(user)[0]?.id ?? null;

}



/**

 * @param {object|null|undefined} user

 * @returns {string}

 */

export function resolveAutoStartDashboardPath(user) {

  return getDefaultDashboardPath(resolvePrimaryRole(user));

}



/**

 * @param {object|null|undefined} user

 * @param {import('./walkthroughTypes').ResolvedWalkthroughTour | import('./walkthroughTypes').WalkthroughTourDef} tour

 * @returns {string}

 */

export function resolveTourStartPath(user, tour) {

  if (tour?.prerequisiteRoute) {

    return tour.prerequisiteRoute;

  }

  return resolveAutoStartDashboardPath(user);

}



/**

 * @param {import('./walkthroughTypes').WalkthroughStepDef | import('./walkthroughTypes').ResolvedWalkthroughStep | null | undefined} step

 * @returns {string | null}

 */

export function resolveStepRoute(step) {

  if (!step) return null;

  if (step.route) return step.route;

  if (step.navItemId) return navItemPath(step.navItemId);

  return null;

}



/**

 * @param {import('./walkthroughTypes').WalkthroughStepDef | import('./walkthroughTypes').ResolvedWalkthroughStep | null | undefined} step

 * @returns {boolean}

 */

export function isChapterBreakStep(step) {

  return step?.stepType === WALKTHROUGH_STEP_CHAPTER_BREAK;

}



/**

 * @param {Object} params

 * @param {string} params.id

 * @param {string} params.nextTourId

 * @param {string} params.title

 * @param {string} [params.body]

 * @param {boolean} [params.optional]

 * @returns {import('./walkthroughTypes').WalkthroughStepDef}

 */

export function createChapterBreakStep({ id, nextTourId, title, body, optional = false }) {
  const chapterName =
    typeof title === 'string' ? title.replace(/^Next:\s*/i, '') : title;

  return {
    id,
    stepType: WALKTHROUGH_STEP_CHAPTER_BREAK,
    nextTourId,
    title,
    body:
      body ??
      t('walkthrough.ui.chapterBreak.body', {
        chapter: chapterName,
      }),
    placement: 'modal',
    optional,
  };
}



/**

 * @param {object|null|undefined} user

 * @returns {boolean}

 */

export function isPmCsgDualRoleUser(user) {

  if (!user) return false;

  const ctx = buildNavContext(user);

  return !!user.isProjectManager && ctx.showCsgNav;

}



/**

 * @param {import('./walkthroughTypes').ResolvedWalkthroughTour[]} tours

 * @param {object|null|undefined} user

 * @returns {{ steps: import('./walkthroughTypes').ResolvedWalkthroughStep[], allowedRoutes: string[] }}

 */

export function buildWalkthroughSequence(tours, user) {

  /** @type {import('./walkthroughTypes').ResolvedWalkthroughStep[]} */

  const steps = [];

  const allowedRoutes = new Set();

  let stepIndex = 0;



  for (let tourIndex = 0; tourIndex < tours.length; tourIndex += 1) {

    const tour = tours[tourIndex];



    if (tourIndex > 0) {

      const optional =

        tour.id === 'csg-operations' && isPmCsgDualRoleUser(user);

      const chapterBreak = createChapterBreakStep({
        id: `chapter-break-${tour.id}`,
        nextTourId: tour.id,
        title: t('walkthrough.ui.chapterBreak.title', { chapter: tour.title }),
        body: t('walkthrough.ui.chapterBreak.body', { chapter: tour.title }),
        optional,
      });

      steps.push({

        ...chapterBreak,

        index: stepIndex,

        tourId: tour.id,

      });

      stepIndex += 1;

    }



    if (tour.prerequisiteRoute) {

      allowedRoutes.add(tour.prerequisiteRoute);

    }



    for (const step of tour.steps) {

      const route = resolveStepRoute(step);

      if (route) {

        allowedRoutes.add(route);

      }



      steps.push({

        ...step,

        index: stepIndex,

        tourId: tour.id,

        tourVersion: tour.version ?? 1,

      });

      stepIndex += 1;

    }

  }



  return {

    steps,

    allowedRoutes: [...allowedRoutes],

  };

}



/**

 * @param {object|null|undefined} user

 * @param {import('./walkthroughTypes').WalkthroughPersistedState} persistedState

 * @returns {{ steps: import('./walkthroughTypes').ResolvedWalkthroughStep[], allowedRoutes: string[], tourIds: string[] }}

 */

export function buildFirstRunSequence(user, persistedState) {

  const tours = getToursForUser(user).filter(

    (tour) => !isTourCompleted(persistedState, tour)

  );



  const { steps, allowedRoutes } = buildWalkthroughSequence(tours, user);



  return {

    steps,

    allowedRoutes,

    tourIds: tours.map((tour) => tour.id),

  };

}



/**

 * @param {import('./walkthroughTypes').WalkthroughStepDef | import('./walkthroughTypes').ResolvedWalkthroughStep | null | undefined} step

 * @param {boolean} isMobile

 * @returns {boolean}

 */

export function stepRequiresMobileNavOpen(step, isMobile) {

  if (!isMobile || !step || isChapterBreakStep(step)) return false;



  const selector = resolveTargetSelector(step, true);

  const anchorId = extractWalkthroughAnchorId(selector);

  if (!anchorId) return false;

  if (MOBILE_NAV_EXEMPT_ANCHORS.has(anchorId)) return false;



  return (

    anchorId === 'nav-sidebar' ||

    anchorId === 'nav-sections' ||

    anchorId.startsWith('nav-section-') ||

    anchorId.startsWith('nav-')

  );

}



const SIDEBAR_EXPAND_EXEMPT_ANCHORS = new Set([

  'nav-mobile-menu',

  'nav-sidebar',

  'nav-sidebar-toggle',

  'theme-profile',

  'welcome-anchor',

]);



/**

 * Expand collapsed desktop sidebar rail so nav walkthrough targets stay visible.

 *

 * @param {import('./walkthroughTypes').WalkthroughStepDef | import('./walkthroughTypes').ResolvedWalkthroughStep | null | undefined} step

 * @param {boolean} isMobile

 * @returns {boolean}

 */

export function stepRequiresSidebarExpand(step, isMobile) {

  if (isMobile || !step || isChapterBreakStep(step)) return false;



  const selector = resolveTargetSelector(step, false);

  const anchorId = extractWalkthroughAnchorId(selector);

  if (!anchorId || SIDEBAR_EXPAND_EXEMPT_ANCHORS.has(anchorId)) return false;



  return (

    anchorId === 'nav-sections' ||

    anchorId.startsWith('nav-section-') ||

    anchorId.startsWith('nav-')

  );

}



/**

 * @param {number} [delayMs=50]

 * @returns {Promise<void>}

 */

export function waitForLayoutPaint(delayMs = 50) {

  return new Promise((resolve) => {

    if (typeof window === 'undefined' || typeof requestAnimationFrame !== 'function') {

      setTimeout(resolve, delayMs);

      return;

    }



    requestAnimationFrame(() => {

      requestAnimationFrame(() => {

        setTimeout(resolve, delayMs);

      });

    });

  });

}



/**

 * @returns {boolean}

 */

export function isPageLoading() {

  if (typeof document === 'undefined') return false;

  return Boolean(document.querySelector(PAGE_LOADER_SELECTOR));

}



/**

 * @param {Object} [options]

 * @param {number} [options.timeoutMs]

 * @param {AbortSignal} [options.signal]

 * @returns {Promise<void>}

 */

export function waitForPageReady({ timeoutMs = PAGE_READY_TIMEOUT_MS, signal } = {}) {

  if (typeof document === 'undefined') {

    return Promise.resolve();

  }



  if (!isPageLoading()) {

    return waitForLayoutPaint();

  }



  return new Promise((resolve, reject) => {

    const startedAt = Date.now();



    const check = () => {

      if (signal?.aborted) {

        reject(new DOMException('Aborted', 'AbortError'));

        return;

      }



      if (!isPageLoading()) {

        waitForLayoutPaint().then(resolve);

        return;

      }



      if (Date.now() - startedAt >= timeoutMs) {

        if (isDevEnvironment) {

          console.warn('[walkthrough] Page loader did not clear before timeout');

        }

        resolve();

        return;

      }



      setTimeout(check, WALKTHROUGH_ROUTE_POLL_MS);

    };



    check();

  });

}



/**

 * @param {string | null | undefined} selector

 * @param {Object} [options]

 * @param {number} [options.timeoutMs]

 * @param {boolean} [options.isMobile]

 * @param {AbortSignal} [options.signal]

 * @returns {Promise<HTMLElement | null>}

 */

export async function waitForSelector(

  selector,

  { timeoutMs = WAIT_FOR_SELECTOR_TIMEOUT_MS, isMobile = false, signal } = {}

) {

  if (!selector || typeof document === 'undefined') {

    return null;

  }



  const startedAt = Date.now();



  while (Date.now() - startedAt < timeoutMs) {

    if (signal?.aborted) {

      return null;

    }



    const el = document.querySelector(selector);

    if (el instanceof HTMLElement) {

      return el;

    }



    await new Promise((resolve) => {

      setTimeout(resolve, WALKTHROUGH_ROUTE_POLL_MS);

    });

  }



  if (isDevEnvironment) {

    console.warn(`[walkthrough] Selector not found after ${timeoutMs}ms: ${selector}`, {

      isMobile,

    });

  }



  return null;

}



/**

 * @param {string | null | undefined} expectedRoute

 * @param {string | null | undefined} actualRoute

 * @param {boolean} isTourNavigating

 * @returns {boolean}

 */

export function isManualNavigationDuringTour(expectedRoute, actualRoute, isTourNavigating) {

  if (!expectedRoute || !actualRoute || isTourNavigating) return false;

  return !isOnRoute(actualRoute, expectedRoute);

}



/**

 * @param {string | null | undefined} currentPath

 * @param {string | null | undefined} targetRoute

 * @returns {boolean}

 */

export function isOnRoute(currentPath, targetRoute) {

  if (!targetRoute) return true;

  if (!currentPath) return false;

  return currentPath === targetRoute || currentPath.startsWith(`${targetRoute}/`);

}



/**

 * @param {Object} params

 * @param {import('./walkthroughTypes').WalkthroughStepDef | import('./walkthroughTypes').ResolvedWalkthroughStep} params.step

 * @param {string} params.currentPath

 * @param {(to: string, options?: { replace?: boolean }) => void} [params.navigate]

 * @param {boolean} [params.isMobile]

 * @param {() => void} [params.openMobileNav]

 * @param {(navigating: boolean) => void} [params.onNavigating]

 * @param {AbortSignal} [params.signal]

 * @returns {Promise<{ skipped: boolean, target: HTMLElement | null, expectedRoute: string | null }>}

 */

export async function prepareStepForDisplay({

  step,

  currentPath,

  navigate,

  isMobile = false,

  openMobileNav,

  expandSidebar,

  isSidebarCollapsed = false,

  onNavigating,

  signal,

}) {

  if (!step || isChapterBreakStep(step)) {

    return { skipped: false, target: null, expectedRoute: resolveStepRoute(step) };

  }



  const expectedRoute = resolveStepRoute(step);

  const selector = resolveTargetSelector(step, isMobile);



  if (expectedRoute && !isOnRoute(currentPath, expectedRoute) && typeof navigate === 'function') {

    onNavigating?.(true);

    try {

      navigate(expectedRoute, { replace: false });

      await waitForPageReady({ signal });

      await waitForLayoutPaint();

    } finally {

      onNavigating?.(false);

    }

  } else if (isPageLoading()) {

    onNavigating?.(true);

    try {

      await waitForPageReady({ signal });

      await waitForLayoutPaint();

    } finally {

      onNavigating?.(false);

    }

  }



  await prepareStepTarget(step, isMobile, {
    openMobileNav,
    expandSidebar,
    isSidebarCollapsed,
  });



  if (!selector) {

    return { skipped: false, target: null, expectedRoute };

  }



  const target = await waitForSelector(selector, { isMobile, signal });

  if (!target) {

    return { skipped: true, target: null, expectedRoute };

  }



  return { skipped: false, target, expectedRoute };

}



/**

 * Opens the mobile navigation drawer when a step targets sidebar content.

 *

 * @param {import('./walkthroughTypes').WalkthroughStepDef | import('./walkthroughTypes').ResolvedWalkthroughStep | null | undefined} step

 * @param {boolean} isMobile

 * @param {{ openMobileNav?: () => void }} [options]

 * @returns {Promise<void>}

 */

export async function prepareStepTarget(step, isMobile, options = {}) {

  const { openMobileNav, expandSidebar, isSidebarCollapsed = false } = options;



  if (stepRequiresMobileNavOpen(step, isMobile) && typeof openMobileNav === 'function') {

    openMobileNav();

    await waitForLayoutPaint();

    return;

  }



  if (

    isSidebarCollapsed &&

    stepRequiresSidebarExpand(step, isMobile) &&

    typeof expandSidebar === 'function'

  ) {

    expandSidebar();

    await waitForLayoutPaint();

  }

}



/**

 * @param {import('./walkthroughTypes').ResolvedWalkthroughStep[]} steps

 * @param {number} currentIndex

 * @returns {number}

 */

export function findNextChapterBreakIndex(steps, currentIndex) {

  for (let index = currentIndex + 1; index < steps.length; index += 1) {

    if (isChapterBreakStep(steps[index])) {

      return index;

    }

  }

  return -1;

}



/**

 * @param {import('./walkthroughTypes').ResolvedWalkthroughStep[]} steps

 * @param {number} chapterBreakIndex

 * @returns {number}

 */

export function findIndexAfterChapterTour(steps, chapterBreakIndex) {

  const chapter = steps[chapterBreakIndex];

  if (!chapter?.nextTourId) {

    return chapterBreakIndex + 1;

  }



  for (let index = chapterBreakIndex + 1; index < steps.length; index += 1) {

    if (steps[index].tourId !== chapter.nextTourId) {

      return index;

    }

  }



  return steps.length;

}



/**

 * @param {string} tourId

 */

export function queueWalkthroughReplay(tourId) {

  if (typeof window === 'undefined' || !tourId) return;

  try {

    window.sessionStorage.setItem(WALKTHROUGH_REPLAY_KEY, tourId);

  } catch {

    // Private browsing — replay may not survive navigation.

  }

}



/**

 * @returns {string|null}

 */

export function consumeWalkthroughReplay() {

  if (typeof window === 'undefined') return null;

  try {

    const tourId = window.sessionStorage.getItem(WALKTHROUGH_REPLAY_KEY);

    if (tourId) {

      window.sessionStorage.removeItem(WALKTHROUGH_REPLAY_KEY);

    }

    return tourId;

  } catch {

    return null;

  }

}



/**

 * @param {Object} params

 * @param {string} params.tourId

 * @param {object} params.user

 * @param {(tourId: string) => void} params.startTour

 * @param {boolean} params.isMobile

 * @param {() => void} [params.openMobileNav]

 * @returns {Promise<void>}

 */

export async function replayWalkthrough({ tourId, user, startTour, isMobile, openMobileNav }) {

  const tour = getTourForUser(user, tourId);

  if (!tour || typeof startTour !== 'function') return;



  const firstStep = tour.steps[0];

  if (firstStep) {

    await prepareStepTarget(firstStep, isMobile, { openMobileNav });

    await waitForLayoutPaint();

  }



  startTour(tourId);

}



/**

 * @param {Object} params

 * @param {object} params.user

 * @param {(tourId: string) => void} params.startTour

 * @param {boolean} params.isMobile

 * @param {() => void} [params.openMobileNav]

 * @returns {Promise<void>}

 */

/**
 * Queue (or immediately start) a walkthrough replay from the app tour button or Help page.
 * Navigates to the tour start route when needed; WalkthroughBootstrap handles cross-route replay.
 *
 * @param {Object} params
 * @param {object} params.user
 * @param {(path: string) => void} [params.navigate]
 * @param {string} [params.tourId]
 * @param {() => void} [params.endActiveTour]
 * @param {(tourId: string) => void} [params.startTour]
 * @param {boolean} [params.isMobile]
 * @param {() => void} [params.openMobileNav]
 * @returns {Promise<boolean>}
 */
export async function requestWalkthroughReplay({
  user,
  tourId,
  navigate,
  endActiveTour,
  startTour,
  isMobile = false,
  openMobileNav,
}) {
  const resolvedTourId = tourId ?? getDefaultAutoStartTourId(user);
  if (!resolvedTourId || !user) return false;

  const tour = getTourForUser(user, resolvedTourId);
  if (!tour) return false;

  if (typeof endActiveTour === 'function') {
    endActiveTour();
  }

  const targetPath = resolveTourStartPath(user, tour);
  const onStartPath =
    typeof window !== 'undefined' && window.location.pathname === targetPath;

  if (!onStartPath && typeof navigate === 'function') {
    queueWalkthroughReplay(resolvedTourId);
    navigate(targetPath);
    return true;
  }

  if (typeof startTour !== 'function') {
    queueWalkthroughReplay(resolvedTourId);
    return true;
  }

  await waitForLayoutPaint();
  await replayWalkthrough({
    tourId: resolvedTourId,
    user,
    startTour,
    isMobile,
    openMobileNav,
  });
  return true;
}

/**
 * @param {Object} params
 * @param {object} params.user
 * @param {(tourId: string) => void} params.startTour
 * @param {boolean} params.isMobile
 * @param {() => void} [params.openMobileNav]
 * @param {() => void} [params.endActiveTour]
 * @returns {Promise<void>}
 */
export async function replayDefaultWalkthrough({
  user,
  startTour,
  isMobile,
  openMobileNav,
  endActiveTour,
}) {
  await requestWalkthroughReplay({
    user,
    startTour,
    isMobile,
    openMobileNav,
    endActiveTour,
  });
}


